import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState({ phase: "checking", order: null });

  useEffect(() => {
    if (!sid) { setState({ phase: "error", order: null }); return undefined; }
    let tries = 0;
    let timer;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (!alive) return;
        if (data?.payment_status === "paid") { clear(); setState({ phase: "paid", order: data.order_number }); return; }
        if (data?.status === "expired") { setState({ phase: "error", order: null }); return; }
      } catch { /* retry */ }
      if (++tries < 10) timer = setTimeout(poll, 2000);
      else if (alive) setState({ phase: "timeout", order: null });
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  return (
    <div className="mx-auto flex min-h-[75vh] max-w-2xl flex-col items-center justify-center px-5 py-24 text-center" data-testid="payment-success-page">
      {state.phase === "checking" && <p className="font-display text-3xl" data-testid="payment-status-checking">Confirming your payment…</p>}
      {state.phase === "paid" && (
        <>
          <motion.div initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 160, damping: 12 }} className="grid h-24 w-24 place-items-center rounded-full bg-[var(--terra)] text-[#241a06]">
            <Check size={44} strokeWidth={2.5} />
          </motion.div>
          <h1 className="font-display mt-10 text-5xl tracking-tight sm:text-6xl">It's <span className="italic text-[var(--terra)]">yours.</span></h1>
          <p className="mt-6 text-[var(--ink-soft)]">Payment confirmed. Your order number is</p>
          <p className="font-mono-x mt-2 text-2xl font-medium" data-testid="order-number">{state.order}</p>
          <p className="mt-4 text-sm text-[var(--ink-soft)]">A receipt is on its way to your inbox from Stripe.</p>
          <Link to="/shop" className="mt-10 rounded-full bg-[var(--ink)] px-8 py-4 text-sm font-semibold text-[var(--bone)]" data-testid="success-continue-shopping">Keep shopping</Link>
        </>
      )}
      {(state.phase === "error" || state.phase === "timeout") && (
        <>
          <h1 className="font-display text-4xl" data-testid="payment-status-error">{state.phase === "timeout" ? "Still processing…" : "We couldn't confirm this payment."}</h1>
          <p className="mt-4 text-[var(--ink-soft)]">{state.phase === "timeout" ? "Refresh this page in a minute to check again." : "If you were charged, please contact us with your email address."}</p>
          <Link to="/checkout" className="link-u mt-8 text-sm font-semibold">Back to checkout</Link>
        </>
      )}
    </div>
  );
}
